import { Button as FdsButton, InlineMessage } from '@fountain/fountain-ui-components';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { ArrowLeftOutlineIcon, Icon } from '@fountain/ripple/icon';
import classNames from 'classnames';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  humanizeKey,
  isBlankConditionValue,
  isDisplayableDate,
} from 'containers/OfferLetterTemplates/constants';
import {
  FieldLabel,
  fieldLabelStyle,
} from 'containers/OfferLetterTemplates/Editor/FieldLabel';
import globalMessages from 'shared/global/messages';

import { formLayout } from './fieldOrder';
import { effectiveFormulaValue, resolveFormulas } from './formula';
import messages from './messages';
import { fieldProblems, signerBlockMessage, signerBlocks } from './readiness';
import { conditionSubtext, documentOnlySubtext } from './subtext';
import { Subtext, TypedField, typedControl, valueLabeler } from './TypedField';
import {
  ApprovalState,
  ConditionField,
  FormField,
  OfferLetterFormData,
  PreviewResult,
  TriggeredRule,
  TypedInput,
} from './types';
import { visibleFields } from './visibility';

export interface SendOfferFormProps {
  form: OfferLetterFormData;
  /** Template field key -> value. Submitted as `field_values`. */
  values: Record<string, string>;
  /** Data key -> value. Submitted as `condition_values` (AXHE-4001). */
  conditionValues: Record<string, string>;
  /**
   * The template field keys the edit report names once this form is saved
   * (`sectionFieldKeys`). Of several fields mapped to one key, these decide the
   * key's sections.
   */
  editedFieldKeys: ReadonlySet<string>;
  /**
   * The last readiness result. Drives the per-field errors and the move to the
   * first flagged field — scroll and focus both (see `revealFirstMissing`); the
   * count message it also produces belongs to the footer (see
   * `SendOfferFormFooterProps`).
   */
  preview?: PreviewResult;
  /**
   * The create/update phase specifically (AXHE-4233). Unlike the footer's
   * `busy` — which also covers the readiness check and only holds the CTA —
   * `locked` inerts the whole surface: every input and the way back to the
   * document list. The check is cancellable, so editing during it just
   * invalidates and is allowed; the create/update is NOT (the POST/PATCH is
   * already with the server), so an edit made during it would be silently
   * clobbered when the response lands. `locked` is always accompanied by
   * `busy`.
   */
  locked?: boolean;
  onFieldChange: (key: string, value: string) => void;
  onConditionChange: (dataKey: string, value: string) => void;
  /**
   * Set only when this form was entered from the document list (AXHE-4181),
   * i.e. the stage holds several customizable documents and the recruiter
   * picked one. The form needs a way back to the list in that case — the
   * template dropdown that used to cover the single-document path is gone
   * entirely (the list is the only way to choose a document now).
   */
  onBackToDocuments?: () => void;
  /** What editing does to the document, above the fields and under the way back. */
  notice?: React.ReactNode;
}

const rulesByField = (approval: ApprovalState): Record<string, TriggeredRule[]> => {
  const grouped: Record<string, TriggeredRule[]> = {};
  approval.triggered_rules.forEach(rule => {
    const key = rule.field_key ?? '__offer__';
    grouped[key] = grouped[key] ?? [];
    grouped[key].push(rule);
  });
  return grouped;
};

/**
 * The menu items to render for a field with a closed value set.
 *
 * A prefilled value that isn't one of the choices still has to appear: MUI
 * renders a select with an unmatched value as an empty box, so the recruiter
 * would see a blank control over a field that in fact holds — and would submit
 * — something else. That happens whenever a mapped field resolves to a value
 * the template's author didn't list, and silently hiding it is how a letter
 * goes out with a value nobody saw. Surfacing it lets them keep it or pick a
 * listed choice instead.
 */
const selectOptions = (
  allowedValues: string[] | null,
  current: string,
): string[] => {
  if (allowedValues == null) return [];
  return current && !allowedValues.includes(current)
    ? [current, ...allowedValues]
    : allowedValues;
};

/**
 * A closed value set of EXACTLY this many answers renders as a radio pair
 * rather than a dropdown: both answers are readable without opening anything
 * and picking one costs a single click. This is the prototype's "Not included /
 * Included" treatment (AXHE-4080 §6), which the shipped form rendered as a
 * select, and it is what a `boolean` field lands on (see `isBooleanChoice`).
 *
 * Three or more stays a select.
 *
 * This is the rule for a TEMPLATE FIELD's own declared value set, and for the
 * boolean pair. A data key renders through `TypedField` instead.
 */
const RADIO_OPTION_COUNT = 2;

/**
 * Whether to hand a date field to the browser's date picker (AXHE-4080 §2) —
 * the prototype's Start Date is a native `type="date"`, where the shipped form
 * had a bare text box.
 *
 * Only when the value the field arrives holding is one the control can display.
 * A native date input given anything else renders EMPTY while the field still
 * holds — and would submit — the original string, so a mapped value the server
 * resolved in some other format would silently disappear behind a blank picker.
 * Same failure the select guards against by surfacing an unlisted value.
 */
const isDateInput = (field: FormField, current: string): boolean =>
  field.data_type === 'date' && (current === '' || isDisplayableDate(current));

/**
 * Whether a field asks for a number — `number`, `currency`, or anything the
 * letter will currency-format (AXHE-4405 §4). All three fell through to a bare
 * text box with nothing to say what a valid answer looked like.
 *
 * `currency_format` is included even when the declared type is something else,
 * because that flag — not `data_type` — is what makes the value numeric by
 * CONTRACT: `MergeValueResolver#format_value` hands any currency_format field to
 * `CurrencyWriter`, which does `BigDecimal(amount.to_s)` and RAISES on a string
 * that isn't a number. The two flags are independent on the server (a `text`
 * field may spell out; a `currency` field may not).
 *
 * ## Why this is `inputMode` and not `type="number"`
 *
 * It was `type="number"`, and that silently corrupted money. A native number
 * input cannot hold a comma and does not reject the keystroke visibly — it fires
 * no change event at all. Reproduced in Chromium and in this repo's own jsdom: a
 * comma-decimal locale typing `75000,50` reports 7, 75, 750, 7500, 75000,
 * (nothing for the comma), 750005, 7500050 — leaving "7500050" in the field and
 * submitting $7,500,050.00 for an offer of $75,000.50, with nothing on screen to
 * show for it. `BigDecimal("7500050")` parses happily, so it reaches a legally
 * binding letter. Pasting the same string blanks the field instead.
 *
 * That is strictly worse than the text box this ticket set out to replace: the
 * old control never lost a character. `inputMode` keeps the part that is
 * unambiguously an improvement — a numeric keypad on touch, a typed hint for
 * assistive tech — without handing the browser permission to rewrite what the
 * recruiter typed.
 *
 * The `BigDecimal` hazard is therefore still open, and still pre-existing.
 * Closing it means validating and normalising the value with a visible message,
 * which needs a product decision about locale and formatting rather than an input
 * that eats characters.
 */
const isNumericField = (field: FormField): boolean =>
  field.data_type === 'number' ||
  field.data_type === 'currency' ||
  field.currency_format;

/**
 * The two answers a `boolean` field stores (AXHE-4405 §4). The radios read Yes
 * and No, which is what the letter prints for either
 * (`OfferLetterTemplates::AnswerText.yes_no`). The stored pair stays, so an
 * offer saved with one keeps its answer selected (`isBooleanChoice`).
 */
const BOOLEAN_OPTIONS = ['True', 'False'];

/**
 * Whether to offer a `boolean` field as the Yes/No pair.
 *
 * Only when the value it arrives holding is one of them — the same rule the date
 * and select branches apply. A mapped boolean resolving to "true", "yes" or "1"
 * would otherwise select neither radio, leaving a blank-looking control over a
 * field that still holds and would submit its original value. Exact match, not
 * case-insensitive: matching loosely would mean either submitting a value the
 * recruiter never saw, or silently rewriting one on a legal document. A text box
 * showing the real value is the honest rendering.
 */
const isBooleanChoice = (field: FormField, current: string): boolean =>
  field.data_type === 'boolean' &&
  (current === '' || BOOLEAN_OPTIONS.includes(current));

/**
 * What to call a field (AXHE-4405 §5). The panel was showing `LETTER_DATE` and
 * `CANDIDATE_NAME` — raw merge keys — because the server sends `label: null` for
 * a field whose template never gave it one, and the fallback was the key itself.
 *
 * `humanizeKey` is the editor rail's own function, so a variable reads the same
 * where it is authored and where it is filled in. That is also why this is here
 * at all: the ticket describes the humanization as something the backend already
 * does and merely fails to carry into this serializer, but the rail humanizes on
 * the CLIENT (`OfferLetterTemplates/constants.ts`) and the server sends the column
 * verbatim. The backend fix is worth doing anyway — it fixes every consumer at
 * once, and is stacked on top of this — but doing it there ALONE would leave this
 * panel wrong until that deploy landed, and doing it here alone would leave the
 * raw key in every other reader.
 *
 * `||` rather than `??` — an empty-string label is as unusable as a missing one.
 */
const fieldLabel = (label: string | null, key: string): string =>
  label || humanizeKey(key);

/**
 * One row of the panel: a label sitting above its control (AXHE-4405 §3).
 *
 * The spacing used to come from each control's own `margin="normal"`, which is
 * MUI's allowance for a floating label that overlaps the field's top border. With
 * the label lifted out to its own element that allowance belongs to the pair, not
 * to the control — otherwise the gap opens up BETWEEN a label and the box it
 * names, which is the one place it must not. Same 16/8 as `normal`, so the
 * panel's rhythm is unchanged.
 */
const PanelField: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Box mt={2} mb={1}>
    {children}
  </Box>
);

const useStyles = makeStyles(theme => ({
  /**
   * The label above a RADIO PAIR (AXHE-4405 §3).
   *
   * The other controls take their label from `FieldLabel` directly; a radio group
   * cannot, because that component renders a `<label>`/`<span>` and a fieldset
   * wants a real `<legend>` — dropping the legend would trade a native
   * association for an ARIA-only one, and MUI's `FormLabel` is also what turns the
   * legend red when the group is in error.
   *
   * So the element stays a legend and only its TYPE is brought into line — from
   * `fieldLabelStyle` itself rather than a copy of its values, so the panel cannot
   * end up carrying two label styles a few pixels apart the next time that one is
   * edited.
   */
  radioLabel: fieldLabelStyle(theme),
  /**
   * The failed-readiness count message, which sits on the footer's grey rather
   * than the content's white (AXHE-4406 moved it there).
   *
   * Not `color="error"`: that resolves to red400, which measures 4.50:1 on white
   * — exactly on WCAG AA's line for 14px text — and only **4.06:1** on gray100,
   * i.e. under it. So the one sentence explaining why the send is blocked is
   * currently the least legible thing in the footer. red600 is 5.52:1 there;
   * `fdsTokens.ts` already reaches for it for the same reason (error text on a
   * tint).
   */
  countMessage: {
    color: theme.palette.common.red600,
  },
  /**
   * The inset around the pinned approval notice. `InlineMessage`'s root is a
   * MUI `Grid container`, i.e. `width: 100%`, so a margin on it cannot shrink
   * it — the inset has to be padding on this wrapper. The horizontal value is
   * `DrawerContent`'s own, so the tint lines up with the fields scrolling above
   * it.
   */
  approvalNotice: {
    padding: theme.spacing(0, 5, 3),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0, 3, 3),
    },
  },
  /**
   * Four corrections `InlineMessage` needs once its message is more than one
   * line, all measured rather than inferred:
   *
   * - It hardcodes `variant="h4"`, which this theme sets at 14px on a 16px
   *   line. That ratio reads visibly tighter than anything else on the panel
   *   once the rule lines are under the sentence. 20px is the nearest step on
   *   the 2px scale to the 19px the prototype sets on both of its lines.
   * - Its icon sits in an `align-items: stretch` row, which overrides the
   *   16x14 the asset declares and stretches it to the full block, centring
   *   the glyph against the middle of the paragraph instead of the first line.
   *   `align-self: flex-start` is the lever that stops that. Giving it the
   *   line's own 20px box is then what aligns it: `contain` keeps the artwork
   *   undistorted and centres it in that box, so the box lines up with the
   *   first line and no offset stands in for it.
   * - The block grows a line per rule, and it is a sibling of the scroll region
   *   in a fixed-height column — so every line it takes comes out of the form
   *   above it. An account with many whole-offer rules would squeeze the form
   *   the warning is about. Five lines is the cap: the sentence, then four
   *   rules, then the text scrolls inside the tint. An edit raises it to seven
   *   (`editApprovalNoticeMessage`).
   * - `body2` carries gray600, which would drop the rule lines off the
   *   `orange800` the tint was contrast-checked against.
   */
  approvalNoticeMessage: {
    '& .MuiTypography-root': {
      lineHeight: '20px',
      maxHeight: 100,
      overflowY: 'auto',
    },
    '& img': {
      alignSelf: 'flex-start',
      height: 20,
      objectFit: 'contain',
      width: 16,
    },
  },
  /**
   * Seven lines for an edit: a held offer's edit in this drawer and the
   * approvals review panel's Edit offer alike. An edit form holds a template's
   * few fields and adds a closing sentence, so it gives up the height rather
   * than hide a rule or that sentence. It ties `approvalNoticeMessage` on
   * specificity and wins by coming later in this sheet.
   */
  editApprovalNoticeMessage: {
    '& .MuiTypography-root': {
      maxHeight: 140,
    },
  },
  approvalRule: {
    color: 'inherit',
    display: 'block',
  },
}));

/** An `aria-describedby` value over the ids that are set, or none. */
const describedByIds = (...ids: (string | undefined)[]): string | undefined =>
  ids.filter(Boolean).join(' ') || undefined;

interface ChoiceFieldProps {
  id: string;
  label: string;
  /** The rule line under the label; see `Subtext`. */
  subtext?: string | null;
  value: string;
  options: string[];
  /** What each option reads as; the option itself is what the form posts. */
  optionLabel?: (option: string) => string;
  error?: boolean;
  helperText?: string;
  /** The AXHE-4233 in-flight lock — see SendOfferFormProps.locked. */
  disabled?: boolean;
  onChange: (value: string) => void;
}

/**
 * A template field's own closed value set, as a radio pair or a dropdown
 * depending on its size. Every such field merges into the document, so none
 * offers an empty answer.
 */
const ChoiceField: React.FC<ChoiceFieldProps> = ({
  id,
  label,
  subtext,
  value,
  options,
  optionLabel = option => option,
  error = false,
  helperText,
  disabled = false,
  onChange,
}) => {
  const classes = useStyles();
  const labelId = `${id}-label`;
  // The id MUI derives for a TextField's own helper text, minted here so both
  // branches below can point at it: neither control announces the helper text
  // on its own, and after a failed readiness check that text is the ONLY
  // statement of why the field was flagged.
  const helperTextId = helperText ? `${id}-helper-text` : undefined;
  const subtextId = `${id}-subtext`;
  const describedBy = describedByIds(subtext ? subtextId : undefined, helperTextId);

  if (options.length !== RADIO_OPTION_COUNT) {
    return (
      <PanelField>
        {/* Above the border, not inside it (AXHE-4405 §3). A select's clickable
            element is a div that only `aria-labelledby` can name, so the label is
            associated from the other end via `labelId` rather than `htmlFor` —
            see FieldLabel. */}
        <FieldLabel id={labelId}>{label}</FieldLabel>
        <Subtext id={subtextId} text={subtext} />
        <TextField
          select
          fullWidth
          variant="outlined"
          // MUI v4 only links a field to its own <input> when an id is given;
          // without it the field is unaddressable for screen readers and for
          // Playwright's getByLabel. Retained alongside `labelId` because it is
          // also the anchor `revealFirstMissing` looks a flagged field up by —
          // on a `select` that id does NOT land on an <input>: `TextField`
          // unsets it there (`InputMore.id = undefined`) and `Select` hands it
          // to `SelectDisplayProps`, so it lands on the visible
          // `div[role="button"]`, which is the element focus can go to.
          id={id}
          // `aria-describedby` is the addition: without it the helper text
          // describes NOTHING a screen reader will ever reach, because
          // `TextField` computes that attribute for the `<Select>` and it gets
          // spread down onto the `<input type="hidden">` carrying the value,
          // never onto the div the recruiter actually focuses. So the one
          // control shape where a flagged field's reason is announced least is
          // the one whose reason for existing is that it holds several answers.
          // (`SelectDisplayProps` is spread onto that div before MUI re-sets its
          // `id`, and `aria-labelledby` is set outside the spread, so neither
          // the anchor nor the accessible name is disturbed.)
          SelectProps={{
            labelId,
            SelectDisplayProps: { 'aria-describedby': describedBy },
          }}
          value={value}
          onChange={e => onChange(e.target.value)}
          error={error}
          helperText={helperText}
          disabled={disabled}
        >
          {options.map(option => (
            <MenuItem key={option} value={option}>
              {optionLabel(option)}
            </MenuItem>
          ))}
        </TextField>
      </PanelField>
    );
  }

  return (
    <PanelField>
      <FormControl
        component="fieldset"
        error={error}
        fullWidth
        // The select branch above puts this id on its visible div; a radio
        // group has no single control to carry it, so it goes on the fieldset.
        // Both branches therefore leave exactly one element addressable as
        // `id` — which is what `revealFirstMissing` looks a flagged field up
        // by, and it must not matter which control the field happened to
        // resolve to. Purely an anchor: the group still takes its accessible
        // name from the legend below, via aria-labelledby, and a <fieldset> is
        // not focusable, so the caret goes to the group's first radio instead
        // (see `focusTarget`).
        id={id}
        // Propagates to both radios via MUI's FormControl context.
        disabled={disabled}
      >
        {/* The radio group takes its accessible name from this legend, which is
            what a select gets from its <label> — so the field stays reachable by
            name whichever control it resolved to. Styled to match FieldLabel; see
            `radioLabel` for why it stays a legend.

            focused={false}: MUI's FormControl tracks focus across its whole
            subtree, so any radio in the group taking keyboard focus would
            otherwise turn this legend FDS blue, regardless of `error`. `error`
            itself is untouched — formControlState resolves each state key
            independently, and this component passes no `error` prop of its
            own, so it still falls through to the FormControl's. */}
        <FormLabel
          component="legend"
          id={labelId}
          className={classes.radioLabel}
          focused={false}
        >
          {label}
        </FormLabel>
        <Subtext id={subtextId} text={subtext} />
        <RadioGroup
          row
          aria-labelledby={labelId}
          // Same gap the select branch above closes: focus lands inside this
          // group, so the helper text has to be the group's description or a
          // screen-reader user hears the question and not the reason it was
          // flagged. Described on the group rather than on each radio so it is
          // announced once, on entry, instead of again on every arrow press.
          aria-describedby={describedBy}
          // Native radio-group traversal is scoped to a shared `name`. MUI v4 does
          // generate one when this is omitted (`useId`, giving `mui-<random>`), so
          // this is not what makes the pair a group — it makes the group's name
          // stable across mounts and legible in a DOM dump, and matches every
          // other RadioGroup in the package.
          name={id}
          value={value}
          onChange={e => onChange(e.target.value)}
        >
          {options.map(option => (
            <FormControlLabel
              key={option}
              value={option}
              control={<Radio size="small" />}
              label={optionLabel(option)}
            />
          ))}
        </RadioGroup>
        {/* Same `<id>-helper-text` shape MUI derives for the TextField branch,
            so one id convention covers every control the form can render — and
            so the test helper that lists the panel's controls can go on
            excluding it with a single suffix rule. */}
        {helperText && (
          <FormHelperText id={helperTextId}>{helperText}</FormHelperText>
        )}
      </FormControl>
    </PanelField>
  );
};

/**
 * An approval rule as one line (AXHE-4080 §5).
 *
 * `description` is empty for every rule the server currently builds —
 * `OfferApproval::Adapter` hardcodes `description: nil` pending AXHE-3929, and
 * `normalizeApproval` turns that into '' — so joining it to `label`
 * unconditionally is not a latent problem but the only thing that renders
 * today: the banner has been showing rule names with a bare trailing colon.
 *
 * (The ticket predicts a literal "null" here instead. That was written against
 * the template literal in the per-field branch without accounting for
 * `normalize.ts`, which never lets a null through. The colon is the real
 * defect; one guard covers both readings.)
 */
const ruleText = (rule: TriggeredRule): string =>
  rule.description ? `${rule.label}: ${rule.description}` : rule.label;

/**
 * Whether every rule an approval names matches whatever the recruiter enters.
 * The preview names those rules' approvers, so on their own they raise no
 * approval notice: the notice waits for a rule a value on the offer tripped.
 * A rule nobody marked counts as one that raises it.
 */
const onlyUnconditionalRules = (approval: ApprovalState): boolean =>
  approval.triggered_rules.length > 0 &&
  approval.triggered_rules.every(rule => rule.conditional === false);

/**
 * Where the caret goes for a flagged field, given the element its id is on.
 *
 * The anchor is the same id in all three shapes, but only two of the three
 * elements it lands on can hold focus: a text box's `<input>` (or an address
 * field's `<textarea>`), and a dropdown's visible `div[role="button"]`, which
 * MUI gives `tabindex="0"`. A radio pair's anchor is the `<fieldset>`, whose
 * `tabIndex` is -1 — `focus()` on it is a silent no-op, exactly the kind of "it
 * looks like it worked" failure this whole function exists to remove — so the
 * caret goes to the group's own tab stop, the first radio.
 *
 * Focusing a radio does NOT select it (only the arrow keys do), so this cannot
 * answer the question on the recruiter's behalf — which matters here, because
 * the field being flagged means the answer is still theirs to give.
 */
const focusTarget = (anchor: HTMLElement): HTMLElement | null =>
  anchor.tabIndex >= 0
    ? anchor
    : anchor.querySelector<HTMLElement>(
        'input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])',
      );

/**
 * Takes the recruiter to the first field a failed readiness check flagged
 * (AXHE-4407).
 *
 * The reveal-order change puts a flagged field near the answer that produced
 * it, which is most of the fix — but "near the answer" is not "on screen", and
 * a long letter's variables still run past the bottom of the panel. A failure
 * message beside the CTA counting fields the recruiter cannot see is the
 * complaint this ticket opened with, one scroll position removed.
 *
 * Focus, not just scroll. A scroll is the whole fix only for someone reading
 * the panel with a mouse; "Preview document" lives in the footer, so a keyboard
 * or screen-reader user who clicks it and is merely scrolled somewhere is left
 * standing on the CTA with no idea the panel moved, let alone which field it
 * moved to. They are the readers this ticket's complaint hits hardest —
 * scanning up the panel for the offending field is precisely what they cannot
 * do — so the field has to come to them. The count message announces itself
 * separately; see `SendOfferFormFooter`.
 *
 * `keysInOrder` is the render order, not the server's — the response lists
 * problems in template-field order, which after the regrouping is not what the
 * recruiter reads top to bottom, and landing them on the second error to scroll
 * past the first would be worse than not scrolling at all.
 *
 * Silent when nothing matches, in both guards. `keysInOrder` is the RENDERED
 * order, so a match is a field that is on screen — but the lookup goes through
 * the DOM, and the caller only knows about problems it was handed. The case that
 * used to arrive here is gone: an unresolvable signature placeholder is reported
 * against a key with no control at all, and it no longer reaches `missing`
 * (AXHE-4441 splits it out before this runs — see `readiness.ts`). What is left
 * is a field the server asked for that the client is a keystroke out of step on.
 * Either way, nothing to go to must mean focus stays where the recruiter left
 * it, rather than being dropped onto the body.
 *
 * `block: 'nearest'` so a field already on screen isn't yanked around: the
 * whole point is to move the panel only when it has to move.
 */
const revealFirstMissing = (
  keysInOrder: string[],
  missing: Set<string>,
  ringColor: string,
) => {
  const first = keysInOrder.find(key => missing.has(key));
  if (first == null) return;

  const element = document.getElementById(`offer-field-${first}`);
  if (element == null) return;

  // `preventScroll` so the scroll below is the only thing that moves the panel.
  // A bare focus() scrolls on its own, instantly and by its own reckoning,
  // which both undoes the smooth animation and overrides the deliberate
  // `block: 'nearest'` politeness a line later.
  const target = focusTarget(element);
  target?.focus({ preventScroll: true });

  // `Mui-focusVisible` is a keyboard-vs-pointer HEURISTIC, not a focus
  // observer: a programmatic focus() called from a click handler never
  // qualifies, no matter how deliberate the move. That leaves exactly the
  // reader this function exists for — see the docstring above — watching
  // their caret land with no visible sign of where. Draw the same ring by
  // hand and drop it on the next blur. `ringColor` comes from the caller
  // rather than a literal here: this module runs outside any component, so
  // `theme.palette.common.blue400` — the same token `theme.options.ts`'s
  // `MuiRadio` override draws the keyboard ring from — is unreachable
  // without it, and a second hard-coded hex is what drifts the day the
  // token moves (recruiter-ui has no `fds-tokens` dependency of its own;
  // see `OfferLetterTemplates/fdsTokens.ts` on why a copied hex is rejected
  // here, PR #647).
  if (target instanceof HTMLInputElement && target.type === 'radio') {
    const ring = target.closest<HTMLElement>('.MuiIconButton-root')?.querySelectorAll('svg');
    for (const svg of ring ?? []) {
      svg.style.outline = `2px auto ${ringColor}`;
      svg.style.outlineOffset = '2px';
    }
    target.addEventListener(
      'blur',
      () => {
        for (const svg of ring ?? []) {
          svg.style.removeProperty('outline');
          svg.style.removeProperty('outline-offset');
        }
      },
      { once: true },
    );
  }

  // jsdom implements no scrolling at all, so the method is simply absent there
  // — and an environment without it should skip the scroll rather than throw
  // through the effect.
  if (typeof element.scrollIntoView !== 'function') return;
  element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
};

export const SendOfferForm: React.FC<SendOfferFormProps> = ({
  form,
  values,
  conditionValues,
  editedFieldKeys,
  preview,
  locked = false,
  onFieldChange,
  onConditionChange,
  onBackToDocuments,
  notice,
}) => {
  const intl = useIntl();
  const theme = useTheme();
  // A Yes/no variable's stored pair, named as the letter prints it.
  const booleanLabel = (option: string): string =>
    intl.formatMessage(option === BOOLEAN_OPTIONS[0] ? globalMessages.yes : globalMessages.no);
  const approval = preview?.approval ?? form.approval;
  const guardrails = rulesByField(approval);
  // Only the problems that name a field on this panel (AXHE-4441). An
  // unresolvable signer has no control to flag and no answer the recruiter could
  // give — it reads in the footer instead; see `readiness.ts`.
  const missingKeys = new Set(fieldProblems(preview).map(m => m.key));

  // AXHE-4543. Derived in render, the same as `askedFields`/`layout` below —
  // not an effect — so it never lags a keystroke on the field a formula
  // reads from.
  const formulaValues = resolveFormulas(form, values);

  const fieldHelper = (field: FormField) => {
    if (missingKeys.has(field.key)) return intl.formatMessage(messages.requiredMissing);
    const rule = guardrails[field.key]?.[0];
    if (rule) return ruleText(rule);
    // Only while the recruiter hasn't entered anything of their own — a blank
    // entry counts as untouched here (matching `effectiveFormulaValue`), not
    // just an absent one, so clearing the field brings this hint back too.
    const formulaUntouched =
      field.formulas.length > 0 && isBlankConditionValue(values[field.key]);
    if (formulaUntouched && formulaValues[field.key] == null) {
      return intl.formatMessage(messages.formulaUnresolvedHint);
    }
    if (formulaUntouched) {
      return intl.formatMessage(messages.formulaCalculatedHint);
    }
    // Below the input, because it answers the edit just made. A blank merged
    // field prints the record's value (`MergeValueResolver#raw_value_for`), so
    // clearing one changes nothing to warn about.
    const current = effectiveFormulaValue(field, values, formulaValues);
    if (isBlankConditionValue(current)) return undefined;
    return documentOnlySubtext(intl, field, current, valueLabeler(intl, field)) ?? undefined;
  };

  // The rule line under an input's label: which blocks this key drives, and on
  // which values, named as the input shows them. Null for a key no block tests.
  const ruleLine = (dataKey: string, input: TypedInput): string | null =>
    conditionSubtext(intl, dataKey, form.conditional_blocks, input.key_type, valueLabeler(intl, input));

  // AXHE-4187. Which variables this template is asking for under the answers so
  // far — "Relocation package" exists on the form only once Work Location says
  // relocation is required, and every variable that lives only inside a section
  // these answers exclude drops off with the section. Re-resolved on every
  // render rather than read from the payload's `visible`, because the payload is
  // from the last request and the reveal has to follow the keystroke that
  // causes it.
  //
  // Hidden fields are dropped from the list, not disabled: a greyed-out input
  // says "part of this offer, not editable right now", when the truth is that
  // the question doesn't apply and its answer will not be merged.
  const askedFields = visibleFields(form, values, conditionValues, editedFieldKeys);

  // AXHE-4407. Where each of those fields goes: a field only on screen because
  // of an answer renders directly under the control that answer belongs to,
  // instead of at the top of the panel while its cause sits at the bottom.
  const layout = formLayout(form, askedFields, values, editedFieldKeys);

  const renderField = (field: FormField) => {
    // AXHE-4543: an untouched (including cleared) formula field shows the
    // computed value rather than the blank/stale `field.value` a
    // manual_entry field starts with — see `effectiveFormulaValue`.
    const current = effectiveFormulaValue(field, values, formulaValues);
    const label = fieldLabel(field.label, field.key);
    // Every mapped field whose value decides its key, wherever it renders. Of
    // two fields sharing a key only the deciding one drives the blocks, so a
    // line under the other would promise an answer that changes nothing.
    const subtext =
      field.data_key && layout.deciders.has(field.key) ? ruleLine(field.data_key, field) : null;

    // An authored `select` set is what the template offers for this field, so
    // it outranks the data key's own type (AXHE-4180).
    if (field.data_type === 'select' && field.allowed_values != null) {
      return (
        <ChoiceField
          key={field.key}
          id={`offer-field-${field.key}`}
          label={label}
          subtext={subtext}
          value={current}
          options={selectOptions(field.allowed_values, current)}
          error={missingKeys.has(field.key)}
          helperText={fieldHelper(field)}
          disabled={locked}
          onChange={value => onFieldChange(field.key, value)}
        />
      );
    }

    // HIR-375 change 2. A field mapped to a data key renders the key's own
    // control — a combobox over the opening's options, a date picker or a
    // Yes/No pair — never a list assembled from the template's conditions. A
    // data key with neither options nor a special type keeps the plain input
    // below, which carries the numeric and address handling.
    if (field.key_type != null && typedControl(field, current) !== 'text') {
      return (
        <TypedField
          key={field.key}
          id={`offer-field-${field.key}`}
          label={label}
          subtext={subtext}
          value={current}
          input={field}
          error={missingKeys.has(field.key)}
          helperText={fieldHelper(field)}
          disabled={locked}
          onChange={value => onFieldChange(field.key, value)}
        />
      );
    }

    // AXHE-4405 §4. A `boolean` was falling through to a text box, where nothing
    // told the recruiter what a valid answer even looked like. Two answers means
    // it lands on the same radio pair as any other two-value choice, so this is
    // the existing control, not a new one.
    if (isBooleanChoice(field, current)) {
      return (
        <ChoiceField
          key={field.key}
          id={`offer-field-${field.key}`}
          label={label}
          subtext={subtext}
          value={current}
          options={BOOLEAN_OPTIONS}
          optionLabel={booleanLabel}
          error={missingKeys.has(field.key)}
          helperText={fieldHelper(field)}
          disabled={locked}
          onChange={value => onFieldChange(field.key, value)}
        />
      );
    }

    const asDate = isDateInput(field, current);
    // AXHE-4405 §4. An address is the one type whose answer is several lines
    // rather than one, and the renderer preserves them:
    // `render_offer_letter_docx.js` runs docxtemplater with `linebreaks: true`,
    // so what is typed here is what the letter shows.
    const asAddress = field.data_type === 'address';
    const fieldId = `offer-field-${field.key}`;
    // `multiline` renders a <textarea>, which has no `type` at all. Otherwise
    // `date` is the only type that changes — a numeric field stays a text input
    // and takes `inputMode`; see `isNumericField`.
    let inputType;
    if (!asAddress) inputType = asDate ? 'date' : 'text';
    const helperText = fieldHelper(field);
    // Set on the <input> itself, which replaces the helper-text id MUI would
    // put there, so both ids go in.
    const describedBy = describedByIds(
      subtext ? `${fieldId}-subtext` : undefined,
      helperText ? `${fieldId}-helper-text` : undefined,
    );

    return (
      <PanelField key={field.key}>
        <FieldLabel htmlFor={fieldId}>{label}</FieldLabel>
        <Subtext id={`${fieldId}-subtext`} text={subtext} />
        <TextField
          // Associates the <label> with the <input> (a11y + getByLabel); see
          // the condition fields below for why MUI v4 needs an explicit id.
          id={fieldId}
          fullWidth
          variant="outlined"
          multiline={asAddress}
          // Enough for a street/city/postcode address without dominating the
          // panel; it does not grow, so a longer one scrolls in place rather than
          // pushing every field below it down as the recruiter types.
          rows={asAddress ? 3 : undefined}
          type={inputType}
          inputProps={{
            ...(isNumericField(field) ? { inputMode: 'decimal' } : {}),
            'aria-describedby': describedBy,
          }}
          value={current}
          onChange={e => onFieldChange(field.key, e.target.value)}
          disabled={locked}
          error={missingKeys.has(field.key)}
          helperText={helperText}
        />
      </PanelField>
    );
  };

  // HIR-375 change 2. The data key's own control, labelled with its title. It
  // arrives holding the applicant's value; a condition-only key can be cleared,
  // and a clear stays empty rather than falling back to the applicant.
  const renderConditionField = (field: ConditionField) => {
    const current = conditionValues[field.key] ?? field.value;
    return (
      <TypedField
        id={`offer-condition-${field.key}`}
        label={fieldLabel(field.label, field.key)}
        subtext={ruleLine(field.key, field)}
        value={current}
        input={field}
        helperText={documentOnlySubtext(intl, field, current, valueLabeler(intl, field)) ?? undefined}
        disabled={locked}
        clearable
        onChange={value => onConditionChange(field.key, value)}
      />
    );
  };

  // The order the recruiter reads the panel in, which is what a flagged field
  // has to be found by — see `revealFirstMissing`.
  const renderedFieldKeys = [
    ...layout.fields,
    ...layout.conditionGroups.flatMap(({ field, revealed }) =>
      field.kind === 'condition' ? revealed : [field, ...revealed],
    ),
  ].map(field => field.key);

  // Once per readiness result, not once per render: `preview` is replaced
  // wholesale by each response and cleared by any edit, so a recruiter fixing
  // one flagged field is never dragged back to another mid-keystroke.
  React.useEffect(
    () => revealFirstMissing(renderedFieldKeys, missingKeys, theme.palette.common.blue400),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [preview],
  );

  return (
    <>
      {onBackToDocuments && (
        <Box mb={2} ml={-1}>
          <Button
            variant="text"
            size="small"
            startIcon={<Icon icon={ArrowLeftOutlineIcon} size="lg" color="inherit" />}
            onClick={onBackToDocuments}
            disabled={locked}
          >
            <FormattedMessage {...messages.backToDocuments} />
          </Button>
        </Box>
      )}

      {notice && <Box mb={3}>{notice}</Box>}

      {/* Fields render directly under the drawer's own "Document details"
          header — no repeated in-panel heading (AXHE-4233). Every field here
          is one no on-screen answer reveals, so this list is stable: the ones
          an answer brings and takes away render beside that answer instead
          (AXHE-4407). */}
      {layout.fields.map(renderField)}

      {/*
        AXHE-4001. These are the data keys the template's conditional
        sections are keyed on — the recruiter answers the question, and the
        sections follow. There is deliberately no per-section switch: one
        answer to "what pay classification is this?" moves every section
        that depends on it, which is what keeps a letter from contradicting
        its own clauses.

        AXHE-4407: the variables an answer reveals now render inside this
        group, each directly beneath the question that revealed it — the
        prototype's "Sign-on bonus → Included → Enter sign-on bonus amount"
        in consecutive rows.
      */}
      {layout.conditionGroups.length > 0 && (
        <>
          <Divider style={{ margin: '20px 0 8px' }} />
          {/* Subordinate to the drawer's own "Document details" title, which is
              the panel's one primary heading. `subtitle1` is 24px in this theme —
              LARGER than that title — so this group read as the top of the panel
              rather than a section of it (AXHE-4405 §1). */}
          <Typography variant="h4">
            <FormattedMessage {...messages.conditionFieldsHeading} />
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <FormattedMessage {...messages.conditionFieldsHint} />
          </Typography>
          {layout.conditionGroups.map(({ field, revealed }) => (
            <React.Fragment key={field.key}>
              {/* HIR-375 change 4: a variable whose data key triggers a block
                  renders here as that key's control, with its typed input. Its
                  answer still travels as the field's own value, which is what
                  reaches the merge. */}
              {field.kind === 'condition' ? renderConditionField(field) : renderField(field)}
              {revealed.map(renderField)}
            </React.Fragment>
          ))}
        </>
      )}

    </>
  );
};

export interface SendOfferApprovalNoticeProps {
  /** The form as loaded. Its approval state answers for the values it arrived with. */
  form: OfferLetterFormData;
  /** The last readiness result. Its approval state supersedes the form's. */
  preview?: PreviewResult;
  /**
   * Whether a field has been edited since the last readiness result. An edit
   * resets `preview`, so without this the notice would fall back to the state
   * the form loaded with and answer for values the recruiter has replaced.
   */
  dirty?: boolean;
  /**
   * The approval to state, from a caller that already knows it. Supersedes
   * `preview` and `form`. The approvals review panel passes the rules an edit
   * asks again, which neither of those can answer: they state what a first
   * send would need.
   */
  approval?: ApprovalState;
  /** A closing line under the rules. */
  note?: React.ReactNode;
  /** The notice of an edit, which shows seven lines before it scrolls, not five. */
  editing?: boolean;
  /** Replaces the container's padding, for a caller with its own gutters. */
  className?: string;
}

/**
 * "Approval required for this document", pinned between the scrolling form and
 * the footer.
 *
 * It renders as a SIBLING of `DrawerContent`, the way `SendOfferFormFooter`
 * already does, so it stays on screen for a long template. As the last block
 * inside the scroll region it was off screen exactly when it mattered — a
 * statement about what pressing the CTA will do, out of sight of the CTA.
 *
 * `InlineMessage` carries the whole warning treatment: the orange100 tint, the
 * warning triangle, and orange800 text at 11.58:1 on that tint. Adopting it
 * takes the design system's radius and padding — 4px, and 8px/12px — over the
 * 5px and uniform 8px the prototype draws.
 *
 * The sentence is what carries the state, not the glyph: the FDS warning
 * asset is #EF9800, which measures 2.09:1 on the tint and so reads as
 * reinforcement rather than as the indicator. It is still what keeps the block
 * off colour alone, and it is the design system's only orange warning icon.
 *
 * `component="div"` because this is a status, not a section title. The drawer
 * heads itself "Document details" at `h3` and its one section "Conditional
 * blocks" at `h4`; `InlineMessage` defaults to an `<h4>`, which would put this
 * sentence beside that section in the heading list a screen reader navigates
 * by.
 *
 * `role="status"` because the block can arrive after the drawer is open: the
 * readiness result supersedes the form's own approval state, so a check the
 * recruiter runs can be what raises the warning. Without a live region that
 * arrival is silent.
 *
 * The notice states approval for values it can answer for, and says nothing
 * otherwise. Approval is resolved server-side, so the two states it can speak
 * to are the values the form loaded with and the values a readiness check
 * reported on. An edit invalidates the readiness result, and the form's own
 * state then describes values the recruiter has already replaced — so between
 * an edit and the next check the notice renders nothing rather than answering
 * for a document that no longer exists. Holding the last result instead would
 * keep a sentence on screen that names the wrong figure.
 *
 * A rule that matches whatever the recruiter enters raises no notice on its
 * own. Once a rule a value on the offer tripped matches, every rule the
 * approval names is listed under the sentence, the one that applies to every
 * offer included.
 */
export const SendOfferApprovalNotice: React.FC<SendOfferApprovalNoticeProps> = ({
  form,
  preview,
  dirty = false,
  approval: given,
  note,
  editing = false,
  className,
}) => {
  const classes = useStyles();
  const approval =
    given ?? preview?.approval ?? (dirty ? undefined : form.approval);

  // The live region is the OUTER element and is always mounted, empty until
  // the notice shows. A region inserted at the same moment as its content is
  // not reliably announced; one that is already there and then fills is. It
  // carries no padding, so it occupies nothing while empty.
  return (
    <div data-testid="offer-approval-status" role="status">
      {approval?.required && !onlyUnconditionalRules(approval) && (
        <div className={className ?? classes.approvalNotice}>
          <InlineMessage
            className={classNames(classes.approvalNoticeMessage, {
              [classes.editApprovalNoticeMessage]: editing,
            })}
            component="div"
            type="orange"
            message={
              <>
                <FormattedMessage {...messages.documentApprovalRequired} />
                {(rulesByField(approval).__offer__ ?? []).map((rule, index) => (
                  <Typography
                    key={`${rule.label}-${index}`}
                    className={classes.approvalRule}
                    component="span"
                    variant="body2"
                  >
                    {ruleText(rule)}
                  </Typography>
                ))}
                {note && (
                  <Typography
                    className={classes.approvalRule}
                    component="span"
                    variant="body2"
                  >
                    {note}
                  </Typography>
                )}
              </>
            }
          />
        </div>
      )}
    </div>
  );
};

export interface SendOfferFormFooterProps {
  /** The last readiness result — its failure count reads above the CTA. */
  preview?: PreviewResult;
  /**
   * Readiness check running OR a create/update in flight (AXHE-4233): one
   * continuous spinner across both phases of a single "Preview document"
   * click, not just the readiness half.
   */
  busy?: boolean;
  /** See SendOfferFormProps.locked. */
  locked?: boolean;
  /**
   * Leaves the drawer without saving (AXHE-4405 §2). Wired to the same
   * dirty-guarded close the X runs, so backing out of a half-filled form
   * confirms exactly as closing it does — the X was previously the only way out
   * at all.
   */
  onCancel: () => void;
  /**
   * Runs the readiness check and, when it clears, proceeds straight to
   * create/update — merging what used to be two buttons ("Check offer" then
   * "Preview offer") into one (AXHE-4233).
   */
  onPreview: () => void;
}

/**
 * The data-collection step's pinned footer (AXHE-4405 §2).
 *
 * Rendered by the container into the drawer's `DrawerFooter` — a sibling of the
 * scrolling content, not part of it — because inline at the end of the form it
 * meant scrolling past every field of a long template to reach the one control
 * that advances the flow.
 *
 * Kept in this file, beside the form it belongs to, so the CTA's disable rule
 * and the readiness message it pairs with stay in one place.
 */
export const SendOfferFormFooter: React.FC<SendOfferFormFooterProps> = ({
  preview,
  busy = false,
  locked = false,
  onCancel,
  onPreview,
}) => {
  const intl = useIntl();
  const classes = useStyles();
  // AXHE-4441. The two kinds of block a failed check can report, which read as
  // separate statements: the signer ones name a role and a remedy that is not on
  // this form, and only the field ones are counted as fields.
  const signers = signerBlocks(preview);
  const fields = fieldProblems(preview);
  // The count line ALSO carries the degenerate case — blocked with nothing
  // itemised, which `missing_fields` from a payload the client couldn't read
  // produces — so this is deliberately not `fields.length > 0`. It reads "0
  // fields still need values", which is poor but visible; going silent in front
  // of a blocked CTA is the failure this ticket removed.
  const showCount = fields.length > 0 || signers.length === 0;

  return (
    <Box width="100%">
      {/* The failure count reads directly above the button it explains. The
          first field it counts is scrolled to and focused — see
          `revealFirstMissing`.

          Announced, not merely rendered: this is the entire result of pressing
          the CTA, and it appears at the far end of the panel from wherever the
          recruiter's caret lands. Without a live region the only readers who
          learn a check failed are the ones who can see the footer.

          The region is mounted with the footer and stays empty until there is
          something to say, rather than being created along with its own text:
          a live region inserted at the same moment as its content is announced
          inconsistently across screen readers, and this one has a container
          that outlives every check to hang it on. An emptied region is silent —
          `aria-relevant` defaults to additions, not removals — so clearing the
          message on the next edit says nothing. */}
      <Box data-testid="offer-readiness-status" role="status">
        {preview && !preview.can_generate && (
          <>
            {/* First, because it is the one the recruiter cannot act on here:
                reading it before the field count is what stops them filling
                everything in and pressing the CTA again to the same refusal. */}
            {signers.map(block => (
              <Typography
                key={block.key}
                variant="body2"
                className={classes.countMessage}
              >
                {/* The role is humanized client-side: an unresolvable role
                    has no roster entry to take a label from, so the key is all
                    there is to name it with, read the same way a field with no
                    authored label is. */}
                <FormattedMessage
                  {...signerBlockMessage(block).message}
                  values={signerBlockMessage(block).values}
                />
              </Typography>
            ))}
            {showCount && (
              <Typography variant="body2" className={classes.countMessage}>
                <FormattedMessage
                  {...messages.cannotSend}
                  values={{ count: fields.length }}
                />
              </Typography>
            )}
          </>
        )}
      </Box>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mt={1} style={{ gap: 12 }}>
        <FdsButton
          type="secondary"
          autoWidth
          onClick={onCancel}
          // Inert while an un-cancellable create/update is in flight, matching
          // the X it shares a close path with: that request will persist or
          // rewrite a draft whatever the client does next, and leaving now
          // abandons a fresh draft with no on-screen trace of it.
          disabled={locked}
        >
          <FormattedMessage {...globalMessages.cancel} />
        </FdsButton>
        <FdsButton
          type="primary"
          autoWidth
          onClick={onPreview}
          // Named explicitly because the label is swapped for a spinner while
          // the check runs, which would otherwise leave the control with no
          // accessible name at exactly the moment a screen reader user is
          // waiting to hear what happened.
          aria-label={intl.formatMessage(messages.previewDocumentCta)}
          // Also disabled while a check/create/update is in flight (busy) so a
          // double-click can't fire a second preview and create a duplicate
          // draft offer before the first response returns. No longer gated on
          // `preview?.can_generate` (AXHE-4233) — the click itself runs
          // readiness and only proceeds when it clears.
          disabled={busy}
        >
          {busy ? <CircularProgress size={18} /> : <FormattedMessage {...messages.previewDocumentCta} />}
        </FdsButton>
      </Box>
    </Box>
  );
};
